import { useMemo, useRef, useState } from 'react';
import { Column } from 'react-table';
import { Container, Col, Row, Card } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import Select from 'react-select';

import { useApiClient } from 'src/hooks/useApiClient';
import ServerPaginatedTable from 'src/components/ServerPaginatedTable/ServerPaginatedTable';
import TableSkeleton from 'src/components/TableSkeleton/TableSkeleton';
import { UpdateConfigurableModal } from 'src/components/CarConfiguratorMotornet/UpdateConfigurableModal';
import {
	MN_MAKES_QUERY_KEY,
	MN_MODELS_QUERY_KEY,
	MN_TRIMS_QUERY_KEY,
	useMakes,
	useModels,
} from 'src/components/CarConfiguratorMotornet/api';
import toaster from 'src/utils/toaster';
import type {
	PlatformConfigurableListItem,
	PlatformConfigurableListResponse,
} from 'src/motornet/types';
import { createColumnsData } from './columnsData';

type VehicleFilter = 'all' | 'configurable' | 'not_configurable';

interface SelectOption {
	value: string;
	label: string;
}

interface StatusOption {
	value: VehicleFilter;
	label: string;
}

const PAGE_SIZE_OPTIONS = [10, 20, 50, 100];
const DEFAULT_PAGE_SIZE = 20;

const filterToExcluded = (filter: VehicleFilter): boolean | undefined => {
	switch (filter) {
		case 'configurable':
			return false;
		case 'not_configurable':
			return true;
		case 'all':
		default:
			return undefined;
	}
};

const QUERY_KEY = 'mn_configurable_list';

const CarConfiguratorMotornetListPage = () => {
	const { apiClient } = useApiClient();
	const { t } = useTranslation();
	const queryClient = useQueryClient();

	const [filter, setFilter] = useState<VehicleFilter>('all');
	const [makeCodeFilter, setMakeCodeFilter] = useState('');
	const [modelCodeFilter, setModelCodeFilter] = useState('');
	const [page, setPage] = useState(1);
	const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);

	const [selectedItem, setSelectedItem] =
		useState<PlatformConfigurableListItem | null>(null);
	const [isModalOpen, setIsModalOpen] = useState(false);

	const excluded = filterToExcluded(filter);

	const { data: makes } = useMakes();
	const { data: models } = useModels(makeCodeFilter || null);
	const makeCode = makeCodeFilter || undefined;
	const modelCode = modelCodeFilter || undefined;

	const makeOptions: SelectOption[] = useMemo(
		() => (makes ?? []).map((m) => ({ value: m.code, label: m.make })),
		[makes]
	);
	const modelOptions: SelectOption[] = useMemo(
		() => (models ?? []).map((m) => ({ value: m.modelCode, label: m.model })),
		[models]
	);
	const selectedMakeOption =
		makeOptions.find((o) => o.value === makeCodeFilter) ?? null;
	const selectedModelOption =
		modelOptions.find((o) => o.value === modelCodeFilter) ?? null;

	const { data, isPending, isFetching, error } =
		useQuery<PlatformConfigurableListResponse>({
			queryKey: [QUERY_KEY, excluded, makeCode, modelCode, page, pageSize],
			queryFn: () =>
				apiClient.motornetControllerService.getPlatformConfigurableList({
					excluded,
					make: makeCode,
					model: modelCode,
					page,
					pageSize,
				}),
			staleTime: 0,
			gcTime: 0,
			retry: false,
			refetchOnWindowFocus: false,
		});

	const items = useMemo(() => data?.items ?? [], [data]);

	const filterKey = `${filter}|${makeCodeFilter}|${modelCodeFilter}`;
	const lastKnownTotal = useRef(0);
	const dataFilter = useRef(filterKey);
	if (data) {
		lastKnownTotal.current = data.total;
		dataFilter.current = filterKey;
	}
	const totalCount = data?.total ?? lastKnownTotal.current;
	const isFilterChanging = filterKey !== dataFilter.current;

	const handleEdit = (item: PlatformConfigurableListItem) => {
		setSelectedItem(item);
		setIsModalOpen(true);
	};

	const handleClose = () => {
		setIsModalOpen(false);
		setSelectedItem(null);
	};

	const excludeMutation = useMutation({
		mutationFn: ({
			vehicleCode,
			configurable,
		}: {
			vehicleCode: string;
			configurable: boolean;
		}) =>
			configurable
				? apiClient.motornetControllerService.unexcludeConfigurable(vehicleCode)
				: apiClient.motornetControllerService.excludeConfigurable(vehicleCode),
		onSuccess: () => {
			toaster.success(t('configurable_updated'));
			queryClient.invalidateQueries({ queryKey: [QUERY_KEY] });
			queryClient.invalidateQueries({ queryKey: [MN_MAKES_QUERY_KEY] });
			queryClient.invalidateQueries({ queryKey: [MN_MODELS_QUERY_KEY] });
			queryClient.invalidateQueries({ queryKey: [MN_TRIMS_QUERY_KEY] });
			handleClose();
		},
		onError: (err: any) => {
			toaster.error(err?.message || t('error_updating_data'));
		},
	});

	const handleConfirm = (configurable: boolean) => {
		if (!selectedItem) return;
		if (!selectedItem.vehicleCode) {
			toaster.error(t('error_updating_data'));
			return;
		}
		excludeMutation.mutate({
			vehicleCode: selectedItem.vehicleCode,
			configurable,
		});
	};

	const columns = useMemo(
		() => createColumnsData(handleEdit) as Column<object>[],
		[] // eslint-disable-line react-hooks/exhaustive-deps
	);

	const statusOptions: StatusOption[] = [
		{ value: 'all', label: t('all') },
		{ value: 'configurable', label: t('configurable') },
		{ value: 'not_configurable', label: t('not_configurable') },
	];
	const selectedStatusOption =
		statusOptions.find((o) => o.value === filter) ?? statusOptions[0];

	const handleFilterChange = (option: StatusOption | null) => {
		setFilter(option?.value ?? 'all');
		setPage(1);
	};

	const handleMakeFilterChange = (option: SelectOption | null) => {
		setMakeCodeFilter(option?.value ?? '');
		setModelCodeFilter('');
		setPage(1);
	};

	const handleModelFilterChange = (option: SelectOption | null) => {
		setModelCodeFilter(option?.value ?? '');
		setPage(1);
	};

	return (
		<Container fluid>
			<Row>
				<Col md={6} className="display-6">
					{t('car_list')}
				</Col>
			</Row>

			<Row className="mt-4 align-items-center">
				<Col md={4}>
					<Select<StatusOption>
						options={statusOptions}
						value={selectedStatusOption}
						onChange={handleFilterChange}
						isSearchable={false}
						formatOptionLabel={(opt, { context }) =>
							context === 'value' && !isFetching
								? `${opt.label} (${totalCount})`
								: opt.label
						}
					/>
				</Col>
				<Col md={4}>
					<Select<SelectOption>
						options={makeOptions}
						value={selectedMakeOption}
						onChange={handleMakeFilterChange}
						placeholder={t('select_brand')}
						isClearable
					/>
				</Col>
				<Col md={4}>
					<Select<SelectOption>
						options={modelOptions}
						value={selectedModelOption}
						onChange={handleModelFilterChange}
						placeholder={t('select_model')}
						isClearable
						isDisabled={!makeCodeFilter}
					/>
				</Col>
			</Row>

			{error ? (
				<Card className="my-4">
					<Card.Body className="text-center text-danger py-5">
						{error instanceof Error && error.message
							? error.message
							: t('error_loading_data')}
					</Card.Body>
				</Card>
			) : isFilterChanging ? (
				<TableSkeleton cols={columns.length} rows={pageSize} />
			) : (
				<ServerPaginatedTable
					key={`${filter}-${pageSize}`}
					columns={columns}
					data={items}
					loading={isPending || isFetching}
					error={error}
					totalCount={totalCount}
					pageSize={pageSize}
					showPageSelector
					pageSizeOptions={PAGE_SIZE_OPTIONS}
					initialPage={page}
					onPageSizeChange={(newSize: number) => {
						setPageSize(newSize);
						setPage(1);
					}}
					loadFn={(skip: number, limit: number) =>
						setPage(Math.floor(skip / limit) + 1)
					}
					dblClickCallback={(item: PlatformConfigurableListItem) =>
						handleEdit(item)
					}
				/>
			)}

			<UpdateConfigurableModal
				isOpen={isModalOpen}
				item={selectedItem}
				saving={excludeMutation.isPending}
				onClose={handleClose}
				onConfirm={handleConfirm}
			/>
		</Container>
	);
};

export default CarConfiguratorMotornetListPage;
